// faqData.ts
import { FaqItem } from "./types";
import { useTranslation } from "react-i18next";

export const useFaqItems = (): FaqItem[] => {
  const { t, i18n } = useTranslation();

  return [
    {
      question: t("What is Hero-Invoice and what can it do?"),
      answer: (
        <>
          <p>
            {t(
              "Hero-Invoice is a browser-based invoicing tool designed for freelancers, small businesses, and creative professionals.",
            )}
          </p>
          <p>{t("With it, you can easily create:")}</p>
          <ul>
            <li>{t("Invoices")}</li>
            <li>{t("Credit notes")}</li>
            <li>{t("Reminders")}</li>
            <li>{t("Estimates")}</li>
            <li>{t("Offers & quotes")}</li>
          </ul>
          <p>
            {t(
              "Each document can be exported as a PDF or e-invoice, printed, or sent directly via email – all from within the tool.",
            )}
          </p>
        </>
      ),
    },
    {
      question: t("Is Hero-Invoice suitable for freelancers and influencers?"),
      answer: (
        <>
          <p>{t("Absolutely!")}</p>
          <p>
            {t(
              "We offer a unique feature that allows influencers to generate invoice/credit note combinations – perfect for scenarios where products are exchanged for social media promotion. This is something most tools don’t offer.",
            )}
          </p>
        </>
      ),
    },
    {
      question: t("Is Hero-Invoice free?"),
      answer: (
        <>
          <p>{t("Absolutely!")}</p>
          <p className=" fw-bold">
            {t("Yes – currently it's 100% free during our beta phase!")}
          </p>
          <p>
            {t(
              "Once the beta ends, we will introduce a super fair pricing model:",
            )}
          </p>
          <ul>
            <li>{t("First 5 invoices per month are always free")}</li>
            <li>{t("Additional invoices cost €0.10 per invoice")}</li>
            <li>{t("Users can purchase credits in advance")}</li>
            <li>
              {t(
                "Large-volume businesses (200+ invoices/month) can request a custom flat rate (max. €20/month)",
              )}
            </li>
            <li>{t("Offers & quotes")}</li>
          </ul>
          <p>
            {t(
              "If you like the tool and want to support its development, you’re more than welcome to make a voluntary donation – every contribution helps us keep things running!",
            )}
          </p>
        </>
      ),
    },
    {
      question: t("Are there Pro features?"),
      answer: (
        <>
          <p>{t("Not yet – but they're coming!")}</p>
          <p className="">
            {t(
              "We're planning additional features to simplify bookkeeping and financial workflows. Our roadmap is shaped by user feedback – so if there's something you’re missing, tell us!",
            )}
          </p>
        </>
      ),
    },
    {
      question: t("Do I need to register?"),
      answer: (
        <>
          <p>
            {t("Yes, you need to create a free account to use Hero-Invoice.")}
          </p>
          <p className="">
            {t(
              "However, since we're currently in open beta, you can access the full range of features without any restrictions – no trial version needed.",
            )}
          </p>
          <p className="">
            {t(
              "After the beta, you'll still be able to send up to 5 invoices per month for free, with no time limit.",
            )}
          </p>
        </>
      ),
    },
    {
      question: t("Are there limitations in the free version?"),
      answer: (
        <>
          <p>{t("Normally, yes – but not during beta!")}</p>
          <ul>
            <li>
              {t(
                "Under regular conditions, you can create up to 5 invoices per month for free. After that, you would need to purchase credits to continue.",
              )}
            </li>
            <li>
              {t(
                "However, since we are currently in open beta, you can create unlimited invoices completely free of charge – no restrictions, no payment required.",
              )}
            </li>
          </ul>
          <p className="">
            {t(
              "Take advantage of the full version while it lasts and help us shape the final product!",
            )}
          </p>
          <p className="">
            {t(
              "If you want to help us grow, feel free to support the project with a donation. We appreciate every bit of encouragement!",
            )}
          </p>
        </>
      ),
    },
    {
      question: t("How secure is my data?"),
      answer: (
        <>
          <ul>
            <li>{t("All data is transmitted securely via encryption")}</li>
            <li>{t("Hosting is provided by Amazon Web Services (AWS)")}</li>
            <li>
              {t(
                "[More details on GDPR compliance and backups will be added soon.]",
              )}
            </li>
          </ul>
        </>
      ),
    },
    {
      question: t("Can I use Hero-Invoice on mobile devices?"),
      answer: (
        <>
          <p>
            {t(
              "Yes! Hero-Invoice is fully responsive and works smoothly on desktops, tablets, and smartphones.",
            )}
          </p>
        </>
      ),
    },
    {
      question: t("What formats can I export?"),
      answer: (
        <>
          <p>
            {t(
              "You can export invoices and related documents as PDFs or electronic invoices (XRechnung, etc.)",
            )}
          </p>
          <p>{t("(Exact formats will be confirmed soon.)")}</p>
        </>
      ),
    },
    {
      question: t("How can I contact support?"),
      answer: (
        <>
          <p>
            {t(
              "You can reach us via email at info@hero-invoice.com – and we typically respond within 24 hours.",
            )}
          </p>
          <p>{t("A help center and tutorials are in the works.")}</p>
        </>
      ),
    },
    {
      question: t("What makes Hero-Invoice different from other tools?"),
      answer: (
        <>
          <ul>
            <li>{t("Transparent pricing – no subscriptions, no tricks")}</li>
            <li>{t("Features for modern professionals, like influencers")}</li>
            <li>{t("Streamlined design – intuitive and distraction-free")}</li>
            <li>
              {t("Built with your feedback in mind – and constantly improving")}
            </li>
          </ul>
        </>
      ),
    },
    {
      question: t("What’s coming next?"),
      answer: (
        <>
          <p>
            {t(
              "We’re currently in beta testing, and we’d love your help. If you encounter issues or have ideas for improvements, send them our way!",
            )}
          </p>
          <p>
            {t(
              "Together, we’re building the invoicing tool we always wanted. Your feedback matters – and if you'd like to support ongoing development, donations are always welcome. Together, we’re building something truly useful.",
            )}
          </p>
        </>
      ),
    },
  ];
};
